import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAngelWithOrganizations } from '../../services/entityService';
import PageContainer from '../../components/layout/PageContainer';
import { CircularProgress, Typography, Box, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { MESSAGES } from '../../constants/messages';

const AngelDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [angelInvestor, setAngelInvestor] = React.useState<any>(null);
    const [organizations, setOrganizations] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (!id) return;
        const fetchAngelAndOrganizations = async () => {
            try {
                const { angel, organizations } = await getAngelWithOrganizations(id);
                setAngelInvestor(angel);
                setOrganizations(organizations);
            } catch (err) {
                setError('Failed to fetch angel investor details.');
            } finally {
                setLoading(false);
            }
        };
        fetchAngelAndOrganizations();
    }, [id]);

    const handleBack = () => {
        navigate(-1);
    };

    if (loading) {
        return <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh"><CircularProgress /></Box>;
    }
    if (error) {
        return <Typography color="error">{error}</Typography>;
    }
    if (!angelInvestor) {
        return <Typography>No angel investor found.</Typography>;
    }

    return (
        <PageContainer>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Button 
                    startIcon={<ArrowBack />} 
                    onClick={handleBack}
                    sx={{ mr: 2 }}
                >
                    Back
                </Button>
                <Typography variant="h4">
                    {angelInvestor.name}
                </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {MESSAGES.PAGE_DISCLAIMER}
            </Typography>
            {angelInvestor.description && <Typography><strong>Description:</strong> {angelInvestor.description}</Typography>}
            {angelInvestor.industries && angelInvestor.industries.length > 0 && <Typography><strong>Industries:</strong> {angelInvestor.industries.join(', ')}</Typography>}
            {Array.isArray(angelInvestor.investment_theme) && angelInvestor.investment_theme.length > 0 && <Typography><strong>Investment Focus:</strong> {angelInvestor.investment_theme.join(", ")}</Typography>}
            {angelInvestor.websites && angelInvestor.websites.length > 0 && (
                <Typography><strong>Websites:</strong> {angelInvestor.websites.map((site: string, index: number) => (
                    <span key={index}>
                        <Link href={site} target="_blank" rel="noopener noreferrer">{site}</Link>
                        {index < angelInvestor.websites.length - 1 && ', '}
                    </span>
                ))}</Typography>
            )}
            {angelInvestor.main_hq && <Typography><strong>Main HQ:</strong> {angelInvestor.main_hq}</Typography>}
            {angelInvestor.locations && angelInvestor.locations.length > 0 && <Typography><strong>Locations:</strong> {angelInvestor.locations.join(", ")}</Typography>}
            {angelInvestor.phone_numbers && angelInvestor.phone_numbers.length > 0 && <Typography><strong>Phone Numbers:</strong> {angelInvestor.phone_numbers.join(", ")}</Typography>}
            {angelInvestor.social_media && angelInvestor.social_media.length > 0 && (
                <Typography><strong>Social Media:</strong> {angelInvestor.social_media.map((social: string, index: number) => (
                    <span key={index}>
                        <Link href={social} target="_blank" rel="noopener noreferrer">{social}</Link>
                        {index < angelInvestor.social_media.length - 1 && ', '}
                    </span>
                ))}</Typography>
            )}
            {angelInvestor.contact_emails && angelInvestor.contact_emails.length > 0 && (
                <Typography><strong>Contact Emails:</strong> {angelInvestor.contact_emails.map((email: string, index: number) => (
                    <span key={index}>
                        <Link href={`mailto:${email}`}>{email}</Link>
                        {index < angelInvestor.contact_emails.length - 1 && ', '}
                    </span>
                ))}</Typography>
            )}
            {Array.isArray(angelInvestor.name_alias) && angelInvestor.name_alias.length > 0 && <Typography><strong>Also known as:</strong> {angelInvestor.name_alias.join(", ")}</Typography>}
            <Box mt={4}>
                <Typography variant="h6" gutterBottom>Organizations Linked to this Angel</Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    To view Persons of Interest associated with this angel investor, click on the organizations below.
                </Typography>
                {organizations && organizations.length > 0 ? (
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Main HQ</TableCell>
                                    <TableCell>Operations in Israel</TableCell>
                                    <TableCell>Unit 8200</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {organizations.map((organization: any) => (
                                    <TableRow key={organization.id}>
                                        <TableCell>
                                            <Link href={`/organization/${organization.id}`}>{organization.name}</Link>
                                        </TableCell>
                                        <TableCell>{organization.industry}</TableCell>
                                        <TableCell>{organization.main_hq}</TableCell>
                                        <TableCell>{organization.has_operations_in_israel ? 'Yes' : 'No'}</TableCell>
                                        <TableCell>{organization.has_8200_connections ? 'Yes' : 'No'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Typography>{MESSAGES.NO_ORGANIZATIONS_FOUND}</Typography>
                )}
            </Box>
        </PageContainer>
    );
};

export default AngelDetail;
